import type { OrdersTranslations } from './types';

/**
 * English translations for Orders domain
 * Comprehensive translations covering all UI text
 */
export const enTranslations: OrdersTranslations = {
  // Page/Section level
  title: 'Orders',
  subtitle: 'Track and manage your cement orders',
  overview: 'Overview',

  // Actions
  addOrder: 'Add Order',
  editOrder: 'Edit Order',
  deleteOrder: 'Delete Order',
  createOrder: 'Create Order',
  newOrder: 'New Order',
  cancel: 'Cancel Order',
  cancelOrder: 'Cancel Order',
  cancelConfirmTitle: 'Cancel Order',
  cancelConfirmDescription:
    'Are you sure you want to cancel order {{orderNumber}}? This action cannot be undone.',
  cancelSuccessDesc: 'Order {{orderNumber}} has been cancelled.',
  cancelError: 'Cancellation Failed',
  cancelErrorDesc: 'Failed to cancel order {{orderNumber}}. Please try again.',
  orderNumberCopied: 'Order number copied',
  copyOrderNumber: 'Copy order number',
  sendSmsToDriver: 'Send SMS to Driver',
  sendingSms: 'Sending...',
  smsSent: 'SMS Sent',
  smsSentDesc: 'The driver has been notified with the order link.',
  smsError: 'SMS Failed',
  smsErrorDesc: 'Failed to send SMS. Please try again.',
  smsNoDriver: 'No driver assigned to this order.',
  downloadPDF: 'Download Order Details',
  downloadQrPDF: 'Download QR PDF',
  downloadDeliveryNote: 'Download Delivery Note',
  previewDeliveryNote: 'Preview delivery note',
  trackOrder: 'Track Order',
  reorder: 'Reorder',

  // Search and filters
  search: 'Search orders...',
  searchPlaceholder: 'Search by order # or SAP Sales Order #...',
  noItemsFound: 'No orders found',
  noItemsDescription:
    "You haven't added any orders yet. Add your first order to get started.",
  addFirst: 'Add Your First Order',
  noOrders: 'No orders found',
  noOrdersDescription: 'No orders match your current filters.',

  // Status labels
  pending: 'Pending',
  processing: 'Processing',
  inTransit: 'In Transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  total: 'Total',
  recent: 'Recent',
  enRoute: 'En Route',

  // Order details
  id: 'Order Number',
  sapSalesOrderNumber: 'SAP Sales Order #',
  orderId: 'Order ID',
  orderDetails: 'Order Details',
  amount: 'Amount',
  quantity: 'Quantity',
  schedule: 'Schedule',
  orderDate: 'Order Date',
  deliveryDate: 'Delivery Date',
  processingToDeliveredTime: 'Processing to Delivered',
  loadingTime: 'Loading Time',
  deliveryAddress: 'Delivery Address',
  paymentStatus: 'Payment Status',
  paid: 'Paid',
  salesOrder: 'Sales Order',
  viewSalesOrder: 'View sales order details',
  salesOrderCreatedBy: 'Sales Order Created By',
  product: 'Product',
  driver: 'Driver',
  factory: 'Plant',
  bags: 'Bags',
  tons: 'Tons',
  kg: 'KG',
  bulk: 'Bulk',
  selectProduct: 'Select Product',
  trackingInfo: 'Tracking Information',
  driverInfo: 'Driver Information',
  notFound: 'Order not found',
  orderNotFoundDescription: 'The order could not be found',

  // Weighbridge
  shipment: 'Shipment',
  weighbridge: 'Weighbridge',
  weighbridgeGross: 'Gross Weight',
  weighbridgeTare: 'Tare Weight',
  weighbridgeNet: 'Net Weight',
  weighbridgeTickets: 'Tickets',
  weighbridgeTimeline: 'Weighbridge Timeline',

  // PDF
  downloadingPDF: 'Downloading PDF',
  downloadingPDFDesc: 'Generating PDF for order {{orderNumber}}...',
  pdfDownloaded: 'PDF Downloaded',
  pdfDownloadedDesc: 'Order {{orderNumber}} PDF has been downloaded',
  pdfDownloadError: 'Download Failed',
  pdfDownloadErrorDesc: 'Failed to download order PDF. Please try again.',
  downloadingDeliveryNote: 'Downloading Delivery Note',
  downloadingDeliveryNoteDesc: 'Generating the delivery note for order {{orderNumber}}...',
  deliveryNoteDownloaded: 'Delivery Note Downloaded',
  deliveryNoteDownloadedDesc: 'The delivery note for order {{orderNumber}} has been downloaded',
  deliveryNoteDownloadError: 'Delivery Note Download Failed',
  deliveryNoteDownloadErrorDesc: 'Failed to download the delivery note. Please try again.',
  pdfPreviewTitle: 'Order PDF',
  pdfPreviewSubtitle: 'Secure preview inside the app',
  pdfPreviewShare: 'Share',
  pdfPreviewRetry: 'Try again',
  pdfPreviewFileName: 'File name',
  pdfPreviewFileSize: 'File size',
  pdfPreviewDismiss: 'Cancel',
  pdfPreviewBadge: 'PDF',
  pdfPreviewReady: 'PDF ready',
  pdfPreviewReadyDesc: 'You can review and share this order PDF.',
  pdfPreviewUnavailableTitle: 'Preview not available',
  pdfPreviewUnavailableDesc:
    "This device can't display PDFs inside the app. Use Share to open the file.",
  pdfShareErrorDesc: 'Unable to share the order PDF. Please try again.',
  pdfShareUnavailableDesc: "Sharing isn't available on this device.",
  deliveryNotePreviewTitle: 'Delivery note',
  deliveryNotePreviewSubtitle: 'Preview and share from this device',
  deliveryNotePreviewDocumentName: 'Document name',
  deliveryNotePreviewDocumentSize: 'Document size',
  deliveryNotePreviewBadge: 'Delivery',
  deliveryNotePreviewReady: 'Delivery note ready',
  deliveryNotePreviewReadyDesc: 'You can review and share this delivery note.',
  deliveryNoteShareErrorDesc: 'Unable to share the delivery note. Please try again.',
  pdfFileSizeUnitB: 'B',
  pdfFileSizeUnitKB: 'KB',
  pdfFileSizeUnitMB: 'MB',
  pdfFileSizeUnitGB: 'GB',

  // Filters
  filterByStatus: 'Filter by Status',
  filterByProduct: 'Filter by Product',
  filterByAmount: 'Filter by Amount',
  filterByAddress: 'Filter by Address',
  filterByDate: 'Filter by Date',
  filterByOrderDate: 'Filter by Order Date',
  filterByDeliveryDate: 'Filter by Delivery Date',
  filterByLP: 'Filter by LP',
  lpPlaceholder: 'Enter license plate',
  filterDescription: 'Refine your orders list using these filters',
  searchProducts: 'Search products...',
  searchAddresses: 'Search addresses...',
  minAmount: 'Min Amount',
  maxAmount: 'Max Amount',
  noLimit: 'No limit',
  selectDateRange: 'Select date range',
  selectStatus: 'Select status',

  // Sort options
  sortByDate: 'Date',
  sortNewest: 'Newest first',
  sortOldest: 'Oldest first',
  sortByOrderNumber: 'Order Number',
  sortByOrderNumberAsc: 'Order # (A-Z)',
  sortByOrderNumberDesc: 'Order # (Z-A)',
  sortByOrderDate: 'Order Date',
  sortByOrderDateAsc: 'Order Date (Earliest)',
  sortByOrderDateDesc: 'Order Date (Latest)',
  sortByDeliveryDate: 'Delivery Date',
  sortByDeliveryDateAsc: 'Delivery (Earliest)',
  sortByDeliveryDateDesc: 'Delivery (Latest)',
  sortByAmount: 'Amount',
  sortByAmountAsc: 'Amount (Low to High)',
  sortByAmountDesc: 'Amount (High to Low)',
  sortByStatus: 'Status',
  sortByStatusAsc: 'Status (A-Z)',
  sortByStatusDesc: 'Status (Z-A)',
  sortByProduct: 'Product',
  sortByProductAsc: 'Product (A-Z)',
  sortByProductDesc: 'Product (Z-A)',
  sortByTenant: 'Tenant',
  sortByTenantAsc: 'Tenant (A-Z)',
  sortByTenantDesc: 'Tenant (Z-A)',

  // New order modal
  addressPlaceholder: 'Select delivery address',
  notes: 'Notes',
  notesPlaceholder: 'Special instructions...',
  estimatedTotal: 'Estimated Total',

  // Form section
  form: {
    name: 'Order Name',
    namePlaceholder: 'Enter order name',
    status: 'Status',
    validation: {
      nameRequired: 'Order name is required',
    },
  },

  // Status (kept for compatibility)
  status: {
    active: 'Active',
    inactive: 'Inactive',
  },

  // Toast messages
  messages: {
    createSuccess: 'Order created successfully',
    updateSuccess: 'Order updated successfully',
    deleteSuccess: 'Order deleted successfully',
    deleteConfirm: 'Are you sure you want to delete this order?',
  },

  // Table headers
  table: {
    name: 'Name',
    status: 'Status',
    factory: 'Plant',
    product: 'Product',
    driver: 'Driver',
    quantity: 'Quantity',
    orderDate: 'Order Date',
    deliveryDate: 'Delivery Date',
    amount: 'Amount',
    actions: 'Actions',
  },

  // Table column labels
  configureColumns: 'Columns',
  selectVisibleColumns: 'Select visible columns',
  actions: 'Actions',
  cancelReasonUserRequested: 'User requested cancellation',

  // Top-level convenience keys
  name: 'Name',
  namePlaceholder: 'Enter order name',
  statusLabel: 'Status',
  nameRequired: 'Order name is required',
  active: 'Active',
  inactive: 'Inactive',
  createSuccess: 'Order created successfully',
  updateSuccess: 'Order updated successfully',
  deleteSuccess: 'Order deleted successfully',
  deleteConfirm: 'Are you sure you want to delete this order?',

  // Bulk actions
  bulkActions: {
    selected: 'selected',
    selectAllOnPage: 'Select all on this page',
    deselectAllOnPage: 'Deselect all on this page',
    selectAllOrders: 'Select all {{count}} orders',
    clearSelection: 'Clear selection',
    downloadPDF: 'Download PDF',
    downloadingPDFs: 'Downloading PDFs',
    preparingPDFs: 'Preparing PDF for {{count}} orders...',
    pdfsDownloaded: 'PDFs Downloaded',
    pdfsDownloadedDesc: 'Successfully downloaded PDF for {{count}} orders',
    pdfDownloadErrorDesc: 'Failed to download orders PDF. Please try again.',
    maxOrdersExceeded: 'Maximum 25 orders can be downloaded at once',
    cancelSelected: 'Cancel Selected',
    cancelSelectedSuccess: 'Cancelled {{count}} selected orders',
    cancelSelectedPartialTitle: 'Bulk cancel completed with issues',
    cancelSelectedPartialDesc:
      'Cancelled {{successCount}} orders, {{failureCount}} failed',
    cancelSelectedError: 'Bulk cancel failed',
    cancelSelectedErrorDesc:
      'Failed to cancel selected orders. Please try again.',
    selectAllAnyStatus: 'Select all on this page (any status)',
    deselectAllAnyStatus: 'Deselect all on this page',
    forceStatusUpdate: 'Force Status Update',
    forceStatusUpdating: 'Updating...',
    forceStatusUpdateSuccess: 'Forced {{count}} orders to {{status}}',
    forceStatusUpdatePartialTitle: 'Force update completed with issues',
    forceStatusUpdatePartialDesc: 'Updated {{successCount}} orders, {{failureCount}} failed',
    forceStatusUpdateError: 'Force update failed',
    forceStatusUpdateErrorDesc: 'Failed to force-update order statuses. Please try again.',
  },

  forceStatusUpdateModal: {
    title: 'Force Status Update',
    description: 'This action bypasses normal order state transitions. It will be logged with full audit details. Use only in authorized emergency or correction scenarios.',
    ordersSummary: '{{count}} order(s) selected',
    targetStatusLabel: 'Target Status',
    targetStatusPlaceholder: 'Select target status',
    reasonLabel: 'Reason (minimum 20 characters)',
    reasonPlaceholder: 'Describe in detail why this forced status change is necessary, what incident or issue triggered it, and who authorized it...',
    reasonMinLength: 'Reason must be at least 20 characters',
    incidentRefLabel: 'Incident / Ticket Reference (optional)',
    incidentRefPlaceholder: 'e.g. INC-1234, JIRA-567',
    affectedOpsNoteLabel: 'Operational Notes (optional)',
    affectedOpsNotePlaceholder: 'Any notes for operations team regarding this override...',
    acknowledgeLabel: 'I confirm this is an authorized override and I accept full accountability for this action',
    confirmTextLabel: 'Type "FORCE UPDATE" to confirm',
    confirmTextPlaceholder: 'FORCE UPDATE',
    confirmTextHint: 'You must type FORCE UPDATE (all caps) to enable the submit button',
    confirmTextMismatch: 'You must type exactly "FORCE UPDATE"',
    submitButton: 'Force Update {{count}} Order(s)',
    submittingButton: 'Updating...',
    cancelButton: 'Cancel',
    warningTitle: 'Irreversible Action',
    warningDesc: 'This will immediately change the status of {{count}} order(s). This action is fully logged and auditable.',
    statusPlanned: 'Planned (Pending)',
    statusProcessing: 'Processing',
    statusDelivered: 'Delivered',
    statusCanceled: 'Cancelled',
    statusFailed: 'Failed',
    currentStatusSummaryLabel: 'Current statuses of selected orders',
  },

  // Timeline tab
  timeline: 'Timeline',
  timelineEmpty: 'No events yet',
  timelineEmptyDesc: 'Events will appear here as the order progresses',
  timelineEntityOrder: 'Order',
  timelineEntityShipment: 'Shipment',
  timelineEntityWeighbridge: 'Weighbridge',
  timelineEntityTransaction: 'Transaction',
  timelineEntityWeightCorrection: 'Weight Correction',
  timelineEntityQueueEntry: 'Queue Entry',
  timelineEntitySalesOrder: 'Sales Order',
  timelineEntityTenant: 'Tenant',
  timelineEntityUser: 'User',
  timelineEntityCustomerAccount: 'Customer Account',
  timelineEntityVariant: 'Variant',
  timelineEntitySku: 'SKU',
  timelineEntityCategory: 'Category',
  timelineEntityLoadingLocation: 'Loading Location',
  timelineActionCreated: 'Created',
  timelineActionUpdated: 'Updated',
  timelineActionDeleted: 'Deleted',
  timelineActionStatusChanged: 'Status Changed',
  timelineActionResourceAssigned: 'Resource Assigned',
  timelineActionResourceChanged: 'Resource Changed',
  timelineActionWeighed: 'Weighed',
  timelineActionWeightCorrected: 'Weight Corrected',
  timelineActionGateOpened: 'Gate Opened',
  timelineActionWeighbridgeTriggered: 'Weighbridge Triggered',
  timelineActionApproved: 'Approved',
  timelineActionSuspended: 'Suspended',
  timelineActionActivated: 'Activated',
  timelineActionBlocked: 'Blocked',
  timelineActionUnblocked: 'Unblocked',
  timelineActionPasswordReset: 'Password Reset',
  timelineActionRoleChanged: 'Role Changed',
  timelineActionChannelChanged: 'Channel Changed',
  timelineActionGroupChanged: 'Group Changed',
  timelineActionKioskScanFailed: 'Scan Failed',

  // Timeline metadata detail labels
  timelineMetaDriver: 'Driver',
  timelineMetaVehicle: 'Vehicle',
  timelineMetaTrailer: 'Trailer',
  timelineMetaNet: 'Net',
  timelineMetaFirst: 'First',
  timelineMetaSecond: 'Second',
  timelineMetaChanged: 'Changed',
  timelineMetaReason: 'Reason',
  timelineMetaNetWeight: 'Net weight',
  timelineMetaForcedBy: 'Forced by',
  timelineMetaIncident: 'Incident',
  timelineMetaOpsNote: 'Ops Note',
  timelineStatusPlanned: 'Planned',
  timelineStatusProcessing: 'Processing',
  timelineStatusDelivered: 'Delivered',
  timelineStatusCanceled: 'Canceled',
  timelineStatusFailed: 'Failed',
  timelineStatusPending: 'Pending',
  timelineStatusLoading: 'Loading',
  timelineStatusInTransit: 'In Transit',
  timelineStatusArrived: 'Arrived',
  timelineStatusCompleted: 'Completed',
  timelineStatusReady: 'Ready',
  timelineStatusEntered: 'Entered',
  timelineStatusWaiting: 'Waiting',
  timelineStageFirstWeight: 'First weight',
  timelineStageSecondWeight: 'Second weight',
  timelineFieldOrderStatus: 'Order status',
  timelineFieldShipmentStatus: 'Shipment status',
  timelineFieldSapSalesOrderNumber: 'SAP sales order number',
  timelineFieldPricingSnapshot: 'Pricing snapshot',
  timelineFieldProcessingStartedAt: 'Processing start time',
  timelineFieldQuantity: 'Quantity',
  timelineFieldDeliveryDate: 'Delivery date',
  timelineFieldCancellationReason: 'Cancellation reason',

  // Audit log event descriptions (i18n keys stored as description in audit log)
  audit: {
    orderCreated: 'Order #{{orderNumber}} created',
    orderStatusChanged: 'Order #{{orderNumber}}: {{previousStatus}} → {{newStatus}}',
    orderUpdated: 'Order #{{orderNumber}} updated',
    orderDeleted: 'Order #{{orderNumber}} deleted',
    shipmentCreated: 'Shipment {{shipmentNumber}} created',
    shipmentCreatedForOrder: 'Shipment {{shipmentNumber}} created for order #{{orderNumber}}',
    shipmentStatusChanged: 'Shipment {{shipmentNumber}}: {{previousStatus}} → {{newStatus}}',
    shipmentUpdated: 'Shipment {{shipmentNumber}} updated',
    shipmentDeleted: 'Shipment {{shipmentNumber}} deleted',
    shipmentResourceAssigned: '{{resourceName}} assigned to {{shipmentNumber}}',
    shipmentResourceChanged: 'Resource changed on {{shipmentNumber}}',
    weighbridgeTareRecorded: 'Tare recorded: {{tare}}',
    weighbridgeGrossRecorded: 'Gross recorded: {{gross}} — net: {{net}}',
    weightCorrected: 'Weight corrected by {{correctedBy}} on {{ticketNumber}}',
    queueTicketIssued: 'Queue ticket {{displayNumber}} issued at {{locationLabel}}',
    queueTicketCancelled: 'Queue ticket {{displayNumber}} cancelled',
    kioskJoinFailed: 'Kiosk scan failed',
    kioskErrors: {
      noResourceAssigned: 'No resource assigned to shipment',
      alreadyInQueue: 'Already in queue for this order',
      vehiclePlateAlreadyInQueue: 'This vehicle is already in the queue',
      noLoadingLocation: 'No loading location available',
      sapSalesOrderFailed: 'SAP sales order creation failed. Please try again or contact support.',
      noActiveContract: 'No active contract found. Please contact your account manager.',
      insufficientContractBalance: 'Insufficient contract balance. Please contact your account manager.',
      noSapMaterialCode: 'Product configuration error. Please contact your account manager.',
    },
    gateOpenedForEntry: 'Gate opened for entry — {{gateLabel}}',
    gateOpenedForExit: 'Gate opened for exit — {{gateLabel}}',
    weighbridgeFlowTriggered: 'Weighbridge flow triggered at gate {{gateLabel}} (stage: {{weightStageLabel}})',
    queueEntryEnteredFactory: '{{displayNumber}} entered factory at gate {{gateId}} ({{fromStatus}} → {{toStatus}})',
    queueEntryExitedFactory: '{{displayNumber}} exited factory ({{fromStatus}} → {{toStatus}})',
  },

  errors: {
    cancellationReasonRequired: 'Cancellation reason is required',
    onlyPlannedCanBeCancelled: 'Only planned orders can be cancelled',
    cannotCancelScannedOrder: 'Scanned orders cannot be cancelled',
    cannotCancelProcessing: 'Cannot cancel an order once processing has started',
    missingTenant: 'Missing tenant on order',
    notFound: 'Order not found',
    notPending: 'Order is not pending',
    cancelFailed: 'Failed to cancel order',
    orderNumberRequired: 'Order number is required',
  },

  export: {
    exportingTitle: 'Exporting...',
    exportingDesc: 'Fetching all records',
    exportingSelected: 'Exporting {{count}} selected order',
    exportingSelected_plural: 'Exporting {{count}} selected orders',
    exportFailedTitle: 'Export failed',
    exportFailed: 'Could not export orders',
  },
  licensePlateExamplePlaceholder: 'e.g. 5080-ZZR',
  subCustomer: 'Customer',
  selectSubCustomer: 'Select Customer',
  changeSubCustomer: 'Change Customer',
  noSubCustomer: 'No customer assigned',
  addNewSubCustomer: 'Add New Customer',
  subCustomerCode: 'Code',
  subCustomerPhone: 'Phone',
  sapSync: 'SAP Sync',
  sapSyncNoJobs: 'No SAP sync jobs found',
  sapSyncNoJobsDescription: 'No SAP sync operations have been recorded for this order.',

  // Support Lookup
  supportLookup: {
    title: 'Order Support Lookup',
    subtitle: 'Search by order number or SAP sales order number to view full order details',
    searchPlaceholder: 'Enter order number or SAP number...',
    search: 'Search',
    searching: 'Searching...',
    orderDetails: 'Order Details',
    warningTitle: 'Support Tool — Handle With Care',
    warningDescription: 'This page allows direct modification of live order data including weighbridge tickets, delivery lines, and timeline events. All changes are logged and irreversible. Only proceed if you fully understand the impact.',
    confirmCancel: 'Cancel',
    confirmConfirm: 'Confirm',
    btnTriggerSap: 'Trigger SAP Sales Order',
    btnTriggering: 'Triggering...',
    sapSyncConfirmTitle: 'Trigger SAP Sales Order Sync?',
    sapSyncConfirmDescription: 'This will send a sync request to SAP for this order. Are you sure you want to proceed?',
    fieldOrderNumber: 'Order Number',
    fieldSapSalesOrder: 'SAP Sales Order',
    fieldStatus: 'Status',
    fieldQuantity: 'Quantity',
    fieldDeliveryDate: 'Delivery Date',
    fieldProcessingStarted: 'Processing Started',
    fieldTenant: 'Tenant',
    fieldFactory: 'Factory',
    fieldProduct: 'Product',
    fieldCity: 'City',
    fieldDropoff: 'Dropoff',
    fieldSubCustomer: 'Sub-Customer',
    fieldSalesOrder: 'Sales Order',
    fieldCreated: 'Created',
    fieldCancellationReason: 'Cancellation Reason',
    sectionShipment: 'Shipment',
    fieldShipmentNumber: 'Shipment Number',
    fieldDispatchedAt: 'Dispatched At',
    fieldArrivedAt: 'Arrived At',
    fieldOrigin: 'Origin',
    fieldDestination: 'Destination',
    fieldHoldReason: 'Hold Reason',
    fieldHoldAt: 'Hold At',
    sectionResource: 'Resource Assignment',
    fieldResourceName: 'Resource Name',
    fieldDriver: 'Driver',
    fieldDriverPhone: 'Driver Phone',
    fieldPowerUnit: 'Power Unit',
    fieldTrailer: 'Trailer',
    sectionWeighbridge: 'Weighbridge Tickets',
    colTicketNumber: 'Ticket #',
    colType: 'Type',
    colGross: 'Gross',
    colTare: 'Tare',
    colNet: 'Net',
    colScale: 'Scale',
    colMeasuredAt: 'Measured At',
    colActions: 'Actions',
    weighbridgeConfirmTitle: 'Update Weighbridge Ticket?',
    weighbridgeConfirmDescription: 'You are about to modify weighbridge data. This will affect net weight calculations. Are you sure?',
    sectionQueue: 'Queue Entry',
    fieldQueueNumber: 'Queue Number',
    fieldLoadingLocation: 'Loading Location',
    fieldVehicle: 'Vehicle',
    fieldRequestedAt: 'Requested At',
    fieldCalledAt: 'Called At',
    fieldEnteredFactory: 'Entered Factory',
    fieldLoadingStarted: 'Loading Started',
    fieldLoadingCompleted: 'Loading Completed',
    fieldExitedFactory: 'Exited Factory',
    fieldEntryGate: 'Entry Gate',
    fieldExitGate: 'Exit Gate',
    sectionDeliveryLines: 'Delivery Lines',
    colDeliveryNumber: 'Delivery #',
    colDeliveryStatus: 'Delivery Status',
    colDeliveredQty: 'Delivered Qty',
    colLineStatus: 'Line Status',
    colPackaging: 'Packaging',
    colCommittedQty: 'Committed Qty',
    deliveryConfirmTitle: 'Update Delivery Line?',
    deliveryConfirmDescription: "You are about to modify a delivery line's quantity or status. This will affect financial calculations and reporting. Are you sure?",
    sectionInvoices: 'Invoices',
    colInvoiceNumber: 'Invoice #',
    colTotal: 'Total',
    colPaid: 'Paid',
    colSapNumber: 'SAP #',
    colIssueDate: 'Issue Date',
    colDueDate: 'Due Date',
    sectionSapSyncJobs: 'SAP Sync Jobs',
    colDirection: 'Direction',
    colOperation: 'Operation',
    colError: 'Error',
    colRetries: 'Retries',
    colCreated: 'Created',
    sectionTimeline: 'Order Timeline',
    placeholderAction: 'Action',
    placeholderDescription: 'Description',
    eventConfirmTitle: 'Update Event?',
    eventConfirmDescription: 'You are about to modify this order timeline event. Are you sure?',
    deleteEventConfirmTitle: 'Delete Event?',
    deleteEventConfirmDescription: 'This will permanently remove the event from the order timeline. This action cannot be undone.',
    btnSave: 'Save',
    btnCancel: 'Cancel',
    btnEdit: 'Edit',
    btnDelete: 'Delete',
  },
};
